import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  AlertTriangle,
  ShieldAlert,
  Activity,
  Search,
  Filter,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { StatCard } from '../components/ui/StatCard';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Skeleton, SkeletonTable } from '../components/ui/Skeleton';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/Table';
import { getTenants, getIncidents } from '../services';
import type { Tenant, EnrichedIncident, PlatformStatus } from '../data/types';

const OPEN_STATUSES: PlatformStatus[] = ['new', 'acknowledged', 'investigating', 'reopened'];

type OnboardingFilter = 'all' | 'active' | 'pending' | 'disabled';

export function TenantList() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [incidentsByTenant, setIncidentsByTenant] = useState<Record<string, EnrichedIncident[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<OnboardingFilter>('all');

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [tenantsResult, incidentsResult] = await Promise.all([
        getTenants(),
        // Open statuses only, matching the Incidents page "All Open" view.
        // The API caps results at 50 rows, so fetching closed history too
        // would both skew the counts and consume the cap.
        getIncidents({ statuses: OPEN_STATUSES }),
      ]);

      if (tenantsResult.success) {
        setTenants(tenantsResult.data);
      } else {
        setError(tenantsResult.error || 'Failed to load customers');
      }

      if (incidentsResult.success) {
        const grouped: Record<string, EnrichedIncident[]> = {};
        incidentsResult.data.forEach(incident => {
          if (!grouped[incident.tenantId]) {
            grouped[incident.tenantId] = [];
          }
          grouped[incident.tenantId].push(incident);
        });
        setIncidentsByTenant(grouped);
      }
    } catch (err) {
      setError('Failed to connect to API');
      console.error('Error fetching customers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredTenants = tenants.filter((tenant) => {
    const matchesSearch =
      searchQuery === '' ||
      tenant.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.tenantId.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesState =
      stateFilter === 'all' || tenant.onboardingState === stateFilter;

    return matchesSearch && matchesState;
  });

  const allIncidents = Object.values(incidentsByTenant).flat();
  const totalActiveIncidents = allIncidents.filter(
    (inc) => inc.platformStatus !== 'closed'
  ).length;
  const totalCriticalHigh = allIncidents.filter(
    (inc) => inc.platformStatus !== 'closed' && (inc.severity === 'critical' || inc.severity === 'high')
  ).length;
  const activeCustomers = tenants.filter(
    (t) => t.onboardingState === 'active'
  ).length;

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
            <Building2 className="w-6 h-6 text-accent-blue" />
            Customers
          </h1>
          <p className="text-text-secondary mt-1">Monitored customer tenants</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-14" />
        <div className="rounded-lg border border-border-default overflow-hidden">
          <SkeletonTable rows={5} columns={5} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
              <Building2 className="w-6 h-6 text-accent-blue" />
              Customers
            </h1>
          </div>
          <Button onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
            Retry
          </Button>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-700 dark:text-red-300 mb-2">Failed to load customers</h3>
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
            <Building2 className="w-6 h-6 text-accent-blue" />
            Customers
          </h1>
          <p className="text-text-secondary mt-1">Monitored customer tenants</p>
        </div>
        <Button variant="secondary" onClick={fetchData}>
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Building2 className="w-5 h-5" />}
          label="Total Customers"
          value={tenants.length}
          accentColor="text-accent-blue"
        />
        <StatCard
          icon={<Activity className="w-5 h-5" />}
          label="Active"
          value={activeCustomers}
          accentColor="text-accent-green"
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Active Incidents"
          value={totalActiveIncidents}
          accentColor="text-accent-amber"
        />
        <StatCard
          icon={<ShieldAlert className="w-5 h-5" />}
          label="Critical / High"
          value={totalCriticalHigh}
          accentColor="text-accent-red"
        />
      </div>

      {/* Search + Filter bar */}
      <div className="glass-card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-bg-tertiary border border-border-default rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as OnboardingFilter)}
              className="pl-10 pr-8 py-2 bg-bg-tertiary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue/50 appearance-none cursor-pointer"
            >
              <option value="all">All States</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customer table */}
      {filteredTenants.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Building2 className="w-12 h-12 text-text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">
            {tenants.length === 0 ? 'No customers found' : 'No matching customers'}
          </h3>
          <p className="text-text-secondary">
            {tenants.length === 0
              ? 'No customers have been onboarded yet.'
              : 'Try adjusting your search or filter.'}
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow hover={false}>
              <TableHead>Customer</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Active Incidents</TableHead>
              <TableHead>Critical / High</TableHead>
              <TableHead className="w-12">&nbsp;</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTenants.map((tenant) => {
              const incidents = incidentsByTenant[tenant.tenantId] || [];
              const activeCount = incidents.filter(
                (inc) => inc.platformStatus !== 'closed'
              ).length;
              const critHighCount = incidents.filter(
                (inc) => inc.platformStatus !== 'closed' && (inc.severity === 'critical' || inc.severity === 'high')
              ).length;

              return (
                <TableRow
                  key={tenant.tenantId}
                  onClick={() => navigate(`/tenants/${tenant.tenantId}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded bg-accent-blue/10">
                        <Building2 className="w-4 h-4 text-accent-blue" />
                      </div>
                      <div>
                        <div className="font-medium text-text-primary">
                          {tenant.displayName}
                        </div>
                        <div className="text-xs text-text-secondary font-mono mt-0.5">
                          {tenant.tenantId}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        tenant.onboardingState === 'active'
                          ? 'green'
                          : tenant.onboardingState === 'pending'
                            ? 'yellow'
                            : 'default'
                      }
                    >
                      {tenant.onboardingState}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">
                      {activeCount > 0 ? (
                        <span className="text-accent-amber font-medium">{activeCount}</span>
                      ) : (
                        <span className="text-text-secondary">0</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">
                      {critHighCount > 0 ? (
                        <span className="text-accent-red font-medium">{critHighCount}</span>
                      ) : (
                        <span className="text-text-secondary">0</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="w-4 h-4 text-text-secondary" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
